import * as R from 'ramda'
import { ofType } from 'redux-observable'
import { empty, of, timer } from 'rxjs'
import { catchError, filter, takeUntil, startWith, switchMap } from 'rxjs/operators'
import { STATUS } from 'constants/checkout'
import {
  checkoutCall,
  checkoutCancelCall,
  checkoutRedirectionCall,
  withoutPackagesCheckoutCall
} from 'api/planet/checkout'
import { getParticipantsBirthdates } from 'domain/participants'
import env from 'helpers/EnvHelper'
import { captureException } from 'helpers/raven'
import * as Actions from 'store/actions/index.ts'
import {
  getActivePackageKey,
  getActivePackageShouldDoubleThePrice,
  getActivePackageTitle,
  getCheckoutRedirection,
  getCountryCode,
  getCultureCode,
  getCurrentProductShouldDoubleThePrice,
  getDeviceType,
  getFirstPackageElementFilteredBySelection,
  getHasPackages,
  getMarketId,
  getProductCallPrice,
  getProductTitle,
  getQuery,
  getSaleKey,
  getSelectedBenefits,
  getSelectedParticipants
} from 'store/selectors'
import { handleUnauthorized } from './helpers'

const redirectTo = uri => {
  if (env.isClientSide()) { window.location = uri }
}

const isCheckoutCancelationAction = ({ type }) => (
  type === Actions.modals.REDIRECTION_MODAL_CLICK_CLOSE
)

const isCheckoutRequested = ({ payload }) =>
  payload && payload.status === STATUS.REQUESTED

const countdown = (
  actions$,
  state$,
  checkoutResponse
) => timer(checkoutResponse.secondsBeforeRedirection * 1000).pipe(
  takeUntil(actions$.pipe(
    filter(isCheckoutCancelationAction),
    switchMap(() => {
      const { requestId } = getCheckoutRedirection(state$.value)
      const packageKey = getActivePackageKey(state$.value)

      return checkoutCancelCall(packageKey, requestId)
    })
  )),
  switchMap(() => {
    const state = state$.value

    const marketId = getMarketId(state)
    const packageKey = getActivePackageKey(state)
    const saleKey = getSaleKey(state)
    const cultureCode = getCultureCode(state)
    const { requestId } = getCheckoutRedirection(state)

    return checkoutRedirectionCall(cultureCode, marketId, packageKey, requestId, saleKey).pipe(
      switchMap(({ response }) => {
        if (response.success && response.redirectTo) {
          return of(Actions.checkout.onCheckoutRedirectionUrlFetched(response))
        } else if (!response.success && response.checkoutDisabled) {
          return (of(Actions.checkout.onCheckoutDisabled(response)))
        }

        captureException(
          new Error('Checkout error: Could not obtain an url after delay'),
          { extra: { checkoutResponse, requestId, response } }
        )

        return of(Actions.checkout.onCheckoutError(new Error('Checkout error: Could not obtain an url after delay')))
      }),
      startWith(Actions.checkout.onCheckoutStatusChanged(STATUS.REDIRECTING)),
      catchError(error => {
        if (error.status === 401) return handleUnauthorized(actions$, state$)

        captureException(
          new Error('Checkout error: Could not obtain an url after delay'),
          { extra: { checkoutResponse, error, requestId } }
        )
        return of(Actions.checkout.onCheckoutError(error))
      })
    )
  }),
  startWith(Actions.checkout.onCheckoutQueued(checkoutResponse))
)

const checkout = (actions$, state$) => {
  const state = state$.value

  const packageTitle = getActivePackageTitle(state)
  const countryCode = getCountryCode(state)
  const cultureCode = getCultureCode(state)
  const deviceType = getDeviceType(state)
  const marketId = getMarketId(state)
  const selectedPackageElement = getFirstPackageElementFilteredBySelection(state)
  const packageKey = getActivePackageKey(state)
  const productTitle = getProductTitle(state)
  const saleKey = getSaleKey(state)
  const { key } = getQuery(state)
  const { version } = state.member

  const participants = getSelectedParticipants(state)
  const participantsBirthdates = getParticipantsBirthdates(participants)
  const adultsBirthdates = participantsBirthdates?.adults
  const babiesBirthdates = participantsBirthdates?.babies
  const childrenBirthdates = participantsBirthdates?.children
  const benefits = getSelectedBenefits(state)
  const shouldDoubleThePrice = getActivePackageShouldDoubleThePrice(state)

  if (!cultureCode || !countryCode || !version) return empty()

  return checkoutCall(
    marketId,
    cultureCode,
    countryCode,
    packageTitle,
    version,
    saleKey,
    key,
    packageKey,
    selectedPackageElement,
    participants,
    adultsBirthdates,
    childrenBirthdates,
    babiesBirthdates,
    deviceType,
    productTitle,
    benefits,
    shouldDoubleThePrice
  ).pipe(
    switchMap(({ response }) => {
      if (response.success) {
        if (R.isEmpty(response.redirectTo)) {
          return countdown(actions$, state$, response)
        }
        if (!response.dataToPost) {
          redirectTo(response.redirectTo)
        }
        return of(Actions.checkout.onCheckoutRedirectionUrlFetched(response))
      } else if (!response.success && response.checkoutDisabled) {
        return of(Actions.checkout.onCheckoutDisabled(response))
      }

      captureException(new Error('Checkout error: response is not success'), {
        extra: {
          adultsBirthdates,
          babiesBirthdates,
          benefits,
          childrenBirthdates,
          key,
          marketId,
          packageKey,
          packageTitle,
          participants,
          productTitle,
          response,
          saleKey,
          selectedPackageElement,
          version
        }
      })

      return of(Actions.checkout.onCheckoutError(new Error('Checkout error: response is not success')))
    }),
    startWith(Actions.checkout.onCheckoutStatusChanged(STATUS.PENDING)),
    catchError(error => {
      if (error.status === 401) { return handleUnauthorized(actions$, state$) }

      captureException(new Error('Unable to call checkout API'), {
        extra: {
          adultsBirthdates,
          babiesBirthdates,
          benefits,
          childrenBirthdates,
          error,
          key,
          marketId,
          packageKey,
          packageTitle,
          participants,
          productTitle,
          saleKey,
          selectedPackageElement,
          version
        }
      })
      return of(Actions.checkout.onCheckoutError(error))
    })
  )
}

const checkoutWithoutPackages = (actions$, state$) => {
  const state = state$.value

  const deviceType = getDeviceType(state)
  const cultureCode = getCultureCode(state)
  const countryCode = getCountryCode(state)
  const marketId = getMarketId(state)
  const price = getProductCallPrice(state)
  const productTitle = getProductTitle(state)
  const saleKey = getSaleKey(state)
  const shouldDoubleThePrice = getCurrentProductShouldDoubleThePrice(state)
  const { key } = getQuery(state)
  const { version } = state.member

  if (!cultureCode || !countryCode || !version) return empty()

  return withoutPackagesCheckoutCall(
    marketId,
    cultureCode,
    countryCode,
    version,
    saleKey,
    key,
    price,
    deviceType,
    productTitle,
    shouldDoubleThePrice
  ).pipe(
    switchMap(({ response }) => {
      if (response.success) {
        if (!response.dataToPost) {
          redirectTo(response.redirectTo)
        }
        return of(Actions.checkout.onCheckoutRedirectionUrlFetched(response))
      }

      captureException(new Error('Checkout error: response is not success'), {
        extra: {
          key,
          marketId,
          price,
          productTitle,
          response,
          saleKey,
          version
        }
      })

      return of(Actions.checkout.onCheckoutError(new Error('Checkout error: response is not success')))
    }),
    startWith(Actions.checkout.onCheckoutStatusChanged(STATUS.PENDING)),
    catchError(error => {
      if (error.status === 401) { return handleUnauthorized(actions$, state$) }

      captureException(new Error('Unable to call checkout API'), {
        extra: {
          error,
          key,
          marketId,
          price,
          productTitle,
          saleKey,
          version
        }
      })
      return of(Actions.checkout.onCheckoutError(error))
    })
  )
}

const checkoutEpic = (actions$, state$) => actions$.pipe(
  ofType(Actions.checkout.CHECKOUT_STATUS_CHANGED),
  filter(isCheckoutRequested),
  switchMap(() => {
    const hasPackages = getHasPackages(state$.value)
    if (hasPackages) { return checkout(actions$, state$) }
    return checkoutWithoutPackages(actions$, state$)
  })
)

export default checkoutEpic
